import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useAppDispatch, useAppSelector } from "../../../redux-store";
import { selectUploadedDocumentsForCargo } from "../../../ducks/data/route-with-stops/selectors";
import { selectMe } from "../../../ducks/auth/selectors";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { StopAttachment } from "../../../ducks/data/route-with-stops/types";
import * as fileUploadActions from "../../../ducks/app/file-uploads";
import "./UploadedDocumentsModal.scss";
import {
  getApiRoot,
  getOrgRefSingleQueryParam,
} from "../../../helpers/apiRequest";
import { isMobile } from "../../../helpers/mobile";
import { useRoutePageContext } from "../RoutePageContext";
import { useParams } from "../../SessionsPage/hooks";

const getApiRootForFiles = () => {
  if (!isMobile) {
    return "/drivers-api/files";
  }
  return `${getApiRoot()}/native-app/drivers-api/files`;
};

const UploadedDocumentsModal = (props: {
  cargoId: string;
  onClose: () => void;
}) => {
  const { t } = useTranslation(["translation", "stopPage"]);
  const uploadedDocuments = useAppSelector(
    selectUploadedDocumentsForCargo(props.cargoId)
  );

  return (
    <Dialog open onClose={props.onClose}>
      <div className="uploaded-documents-modal">
        <div className="uploaded-documents-modal__title">
          {t("stopPage:uploadedDocuments")}
        </div>
        <div className="uploaded-documents-modal__documents">
          {uploadedDocuments.map((doc) => (
            <UploadedDocument key={doc.fileId} document={doc} />
          ))}
        </div>
        <Button variant="outlined" onClick={props.onClose}>
          {t("translation:Close")}
        </Button>
      </div>
    </Dialog>
  );
};

const UploadedDocument = ({ document }: { document: StopAttachment }) => {
  const { t } = useTranslation(["translation", "stopPage", "components"]);
  const dispatch = useAppDispatch();
  const { reloadData } = useRoutePageContext();
  const myDriverId = useAppSelector(selectMe)?.driverId;
  const params = useParams();
  const truckId = params.truckId as string;
  const [loadedState, setLoadedState] = useState<
    "PENDING" | "LOADED" | "ERROR" | "NONE"
  >("PENDING");
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const onRetryClick = () => {
    setLoadedState("NONE");
    setTimeout(() => setLoadedState("PENDING"), 200);
  };

  const onConfirmDelete = async () => {
    setDeleting(true);
    try {
      await dispatch(
        fileUploadActions.deleteFileReference({
          fileId: document.fileId,
          truckId,
        })
      ).unwrap();
      setShowConfirmDelete(false);
      reloadData();
    } catch {
      // Already reported and shown by the thunk.
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <Dialog
        open={showConfirmDelete}
        onClose={() => setShowConfirmDelete(false)}
      >
        <DialogTitle>
          {t("stopPage:confirmDeleteDocumentDialog.title")}
        </DialogTitle>
        <DialogContent>
          {t("stopPage:confirmDeleteDocumentDialog.body")}
        </DialogContent>
        <DialogActions>
          <div className="flex gap-2 justify-between w-100 mt-4">
            <Button
              variant="outlined"
              disabled={deleting}
              onClick={() => setShowConfirmDelete(false)}
            >
              {t("components:buttonLabels.cancel")}
            </Button>
            <Button
              variant="contained"
              color="error"
              loading={deleting}
              disabled={deleting}
              onClick={onConfirmDelete}
            >
              {t("components:buttonLabels.delete")}
            </Button>
          </div>
        </DialogActions>
      </Dialog>
      <div className="flex justify-between items-center">
        <div>{document.name}</div>
        {document.driverId === myDriverId && (
          <IconButton
            size="medium"
            color="error"
            aria-label={t("components:buttonLabels.delete")}
            onClick={() => setShowConfirmDelete(true)}
          >
            <DeleteOutlineIcon fontSize="medium" />
          </IconButton>
        )}
      </div>
      <div>
        {document.previewId ? (
          <>
            {loadedState === "PENDING" ? (
              <div className="uploaded-documents-modal__loading-error-container">
                <CircularProgress />
              </div>
            ) : null}
            {loadedState === "ERROR" ? (
              <div className="uploaded-documents-modal__loading-error-container">
                <div className="text-center">
                  <div className="mb-2" style={{ color: "var(--error-500)" }}>
                    {t("stopPage:documentFetchError")}
                  </div>
                  <Button variant="outlined" onClick={onRetryClick}>
                    {t("translation:Retry")}
                  </Button>
                </div>
              </div>
            ) : null}

            {loadedState !== "ERROR" && loadedState !== "NONE" ? (
              <img
                alt=""
                onLoad={() => setLoadedState("LOADED")}
                onError={() => setLoadedState("ERROR")}
                style={{
                  width: "100%",
                  display: loadedState === "LOADED" ? "block" : "none",
                }}
                src={`${getApiRootForFiles()}/${document.previewId}${getOrgRefSingleQueryParam()}`}
              />
            ) : null}
            {loadedState === "NONE" ? (
              <div className="uploaded-documents-modal__loading-error-container"></div>
            ) : null}
          </>
        ) : (
          <div className="uploaded-documents-modal__loading-error-container">
            {t("stopPage:noPreviewAvailable")}
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadedDocumentsModal;
